import { ReactNode, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Kbd } from '@/components/ui/kbd';
import useOperatingSystem from '@/hooks/useOperatingSystem';
import { getPermissions } from '@/redux/permissions/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { CopiedObject } from '@/util/CopyPasteController';

export type ActionOption = {
  label: string;
  shortcut: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  requiresWrite?: boolean;
};

type Props = {
  trigger: ReactNode;
  options?: ActionOption[];
  handleDelete: () => void;
  handleAddComment?: () => void;
  handleCopy?: (obj: CopiedObject) => void;
  handlePaste?: () => void;
  objectToCopy?: CopiedObject;
  disableCopy?: boolean;
};

// The card clips its content, so an outset ring gets sliced off at the edge.
export const menuTriggerClassName = 'focus-visible:ring-offset-0 focus-visible:ring-inset';

const renderShortcut = (shortcut: string) => {
  const parts = shortcut.split(' + ');
  return (
    <span className="ml-auto inline-flex items-center gap-1">
      {parts.map((part, index) => (
        <span key={part} className="inline-flex items-center gap-1">
          {index > 0 && <span className="text-muted-foreground">+</span>}
          <Kbd className="bg-background text-xs">{part}</Kbd>
        </span>
      ))}
    </span>
  );
};

export function ActionMenu({
  trigger,
  options = [],
  handleDelete,
  handleCopy,
  handlePaste,
  objectToCopy,
  disableCopy = false,
}: Props) {
  const { metaKey, deleteShortcut } = useOperatingSystem();
  const { t } = useTranslation();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );

  const defaultOptions = useMemo<ActionOption[]>(
    () => [
      {
        label: t('dialog.actions.menu.copy'),
        shortcut: `${metaKey} + C`,
        onClick: () => handleCopy(objectToCopy),
        disabled: disableCopy,
      },
      {
        label: t('dialog.actions.menu.paste'),
        shortcut: `${metaKey} + V`,
        onClick: handlePaste,
        requiresWrite: true,
      },
      {
        label: t('dialog.actions.menu.delete'),
        shortcut: deleteShortcut,
        onClick: handleDelete,
        className: 'danger',
        requiresWrite: true,
      },
    ],
    [t, metaKey, disableCopy, handlePaste, deleteShortcut, handleDelete, handleCopy, objectToCopy]
  );

  const renderOptions = options.length ? options : defaultOptions;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-fit gap-2 p-2">
        {renderOptions.map((option) => (
          <DropdownMenuItem
            key={option.label}
            disabled={option.disabled || (option.requiresWrite && !canWrite)}
            onSelect={option.onClick}
            variant={option.className === 'danger' ? 'destructive' : 'default'}
            className="flex justify-between gap-4"
          >
            <span>{option.label}</span>
            {renderShortcut(option.shortcut)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
